import { API_BASE, isNativePlatform } from '../platform'
import type { ApiEnvelope, Locale } from './types'

// Native (Capacitor) apps can't run the domain-locked reCAPTCHA; they present this shared key
// so the backend bypasses reCAPTCHA on confirm/capture. Only baked into the native build.
const NATIVE_KEY = import.meta.env.VITE_NATIVE_KEY as string | undefined

export class ApiError extends Error {
  code: string
  constructor(message: string, code = 'ERROR') {
    super(message)
    this.code = code
  }
}

let currentLocale: Locale = 'it'
export function setApiLocale(locale: Locale) {
  currentLocale = locale
}

const TOKEN_KEY = 'gsm_token'
function authToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}
export function getAuthToken(): string | null {
  return authToken()
}
export function setAuthToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* ignore */
  }
}
export function clearAuthToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

function headers(json: boolean, noAuth = false): Record<string, string> {
  const h: Record<string, string> = {
    // The backend resolves locale from the `language` header (it | en | de).
    language: currentLocale,
    Accept: 'application/json',
  }
  if (json) h['Content-Type'] = 'application/json'
  // Optional JWT — backend reads a custom `mobile-token` header (not Bearer).
  // Auth calls (login/register) must NOT send it, or the firewall short-circuits.
  if (!noAuth) {
    const token = authToken()
    if (token) h['mobile-token'] = token
  }
  // Native build only: lets the backend skip reCAPTCHA (which can't run in the WebView).
  if (NATIVE_KEY && isNativePlatform()) h['gsm-native-key'] = NATIVE_KEY
  // This app uses its own reCAPTCHA registration (VITE_RECAPTCHA_SITE_KEY); flag the request so
  // the backend verifies the token against the matching app secret, not the legacy site's key.
  h['gsm-rc'] = 'app'
  return h
}

/**
 * Core request. The backend ALWAYS returns HTTP 200 with an envelope
 * { result, code, data }; success is `result === "SUCCESS"`. We unwrap to `data`.
 */
async function request<T>(path: string, init?: RequestInit & { json?: unknown; noAuth?: boolean }): Promise<T> {
  const isJson = init?.json !== undefined
  const res = await fetch(`${API_BASE}${path}`, {
    method: init?.method ?? (isJson ? 'POST' : 'GET'),
    headers: { ...headers(isJson, init?.noAuth), ...(init?.headers as Record<string, string>) },
    body: isJson ? JSON.stringify(init?.json) : init?.body,
    credentials: 'include',
  })

  if (!res.ok) {
    throw new ApiError(`HTTP ${res.status}`, `HTTP_${res.status}`)
  }

  let payload: ApiEnvelope<T>
  try {
    payload = (await res.json()) as ApiEnvelope<T>
  } catch {
    throw new ApiError('Invalid JSON response')
  }

  if (!payload || payload.result !== 'SUCCESS') {
    throw new ApiError(payload?.code ?? 'Request failed', payload?.code ?? 'ERROR')
  }
  return payload.data
}

export const http = {
  get: <T>(path: string, opts?: { noAuth?: boolean }) => request<T>(path, { ...opts }),
  post: <T>(path: string, json: unknown, opts?: { noAuth?: boolean }) =>
    request<T>(path, { method: 'POST', json, ...opts }),
}
